import { useState } from 'react';
import { ArrowUp, ArrowDown, Trash2, Plus, PenSquare } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ImageUpload from './ImageUpload';
import MapEditor from './MapEditor';

const TYPES = [
  { value: 'heading', label: 'Titolo' },
  { value: 'paragraph', label: 'Paragrafo' },
  { value: 'box_remember', label: 'Box "Da ricordare"' },
  { value: 'box_warning', label: 'Box "Attenzione"' },
  { value: 'table', label: 'Tabella' },
  { value: 'image', label: 'Immagine' },
  { value: 'concept_map', label: 'Mappa concettuale' },
];

const DEFAULTS = {
  heading: { type: 'heading', level: 2, text: '' },
  paragraph: { type: 'paragraph', text: '' },
  box_remember: { type: 'box_remember', text: '' },
  box_warning: { type: 'box_warning', text: '' },
  table: { type: 'table', headers: ['Colonna 1', 'Colonna 2'], rows: [['', '']] },
  image: { type: 'image', url: '', alt: '', caption: '' },
  concept_map: { type: 'concept_map', data: { title: 'Mappa concettuale', root: { label: 'Nodo radice', children: [] } } },
};

const typeLabel = (t) => TYPES.find((x) => x.value === t)?.label || t;

function TableEditor({ block, onChange }) {
  const setHeaders = (v) => onChange({ ...block, headers: v.split(',').map((s) => s.trim()) });
  const setRows = (v) => onChange({ ...block, rows: v.split('\n').filter((r) => r.trim()).map((r) => r.split(',').map((s) => s.trim())) });
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Intestazioni (separate da virgola)</Label>
        <Input className="mt-1" value={(block.headers || []).join(', ')} onChange={(e) => setHeaders(e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Righe (una per linea, celle separate da virgola)</Label>
        <Textarea className="mt-1 font-mono text-xs" rows={4}
          value={(block.rows || []).map((r) => r.join(', ')).join('\n')}
          onChange={(e) => setRows(e.target.value)} />
      </div>
    </div>
  );
}

function BlockFields({ block, onChange }) {
  const [mapOpen, setMapOpen] = useState(false);
  if (block.type === 'heading') {
    return (
      <div className="flex gap-3">
        <Select value={String(block.level || 2)} onValueChange={(v) => onChange({ ...block, level: Number(v) })}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="2">H2</SelectItem>
            <SelectItem value="3">H3</SelectItem>
          </SelectContent>
        </Select>
        <Input className="flex-1" placeholder="Testo del titolo" value={block.text || ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })} />
      </div>
    );
  }
  if (block.type === 'table') return <TableEditor block={block} onChange={onChange} />;
  if (block.type === 'image') {
    return (
      <div className="space-y-3">
        <ImageUpload value={block.url} onUploaded={(url) => onChange({ ...block, url })} label="Carica immagine" testId="block-image-upload" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Testo alternativo" value={block.alt || ''} onChange={(e) => onChange({ ...block, alt: e.target.value })} />
          <Input placeholder="Didascalia (opzionale)" value={block.caption || ''} onChange={(e) => onChange({ ...block, caption: e.target.value })} />
        </div>
      </div>
    );
  }
  if (block.type === 'concept_map') {
    return (
      <div className="space-y-3">
        <Button type="button" variant="outline" onClick={() => setMapOpen(true)} data-testid="open-map-editor" className="rounded-xl">
          <PenSquare size={14} strokeWidth={1.5} className="mr-1.5" /> Apri editor visuale mappa
        </Button>
        <Textarea className="font-mono text-xs" rows={6}
          value={block._raw ?? JSON.stringify(block.data || {}, null, 2)}
          onChange={(e) => {
            try { onChange({ ...block, data: JSON.parse(e.target.value), _raw: undefined }); } catch { onChange({ ...block, _raw: e.target.value }); }
          }} />
        <Dialog open={mapOpen} onOpenChange={setMapOpen}>
          <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader><DialogTitle className="font-display">Editor mappa concettuale</DialogTitle></DialogHeader>
            <MapEditor data={block.data} onSave={(d) => { onChange({ ...block, data: d, _raw: undefined }); setMapOpen(false); }} />
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  return (
    <Textarea rows={3} placeholder="Testo..." value={block.text || ''}
      onChange={(e) => onChange({ ...block, text: e.target.value })} />
  );
}

export default function BlockEditor({ value, onChange }) {
  const blocks = value || [];
  const [newType, setNewType] = useState('paragraph');

  const update = (i, b) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const arr = [...blocks];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const add = () => onChange([...blocks, { ...DEFAULTS[newType] }]);

  return (
    <div className="space-y-3" data-testid="block-editor">
      {blocks.length === 0 && (
        <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl p-4 text-center">
          Nessun blocco. Aggiungi il primo qui sotto.
        </p>
      )}
      {blocks.map((b, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50" data-testid={`block-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{i + 1} · {typeLabel(b.type)}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500" data-testid={`block-up-${i}`}><ArrowUp size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500" data-testid={`block-down-${i}`}><ArrowDown size={13} /></button>
              <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`block-delete-${i}`}><Trash2 size={13} /></button>
            </div>
          </div>
          <BlockFields block={b} onChange={(nb) => update(i, nb)} />
        </div>
      ))}
      <div className="flex gap-2">
        <Select value={newType} onValueChange={setNewType}>
          <SelectTrigger className="flex-1" data-testid="block-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" onClick={add} data-testid="block-add" className="rounded-xl">
          <Plus size={14} strokeWidth={1.5} className="mr-1" /> Aggiungi
        </Button>
      </div>
    </div>
  );
}
